import React, { useCallback, useState, KeyboardEvent } from "react";
import { map, get, isEmpty, groupBy, debounce, orderBy } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faSearch,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { useQuery } from "react-query";
import { stringify } from "query-string";

import ChatHeader from "../shared/ChatHeader";
import GroupedItems from "../shared/GroupedItems";
import Tile from "./Tile";
import { BackToRootButton } from "../shared/BackButton";
import PageLoading from "components/shared/PageLoading";

type Item = {
  id: string;
  name: string;
  type: "member" | "group";
  [key: string]: any;
};

function useAvailableChats(q: string) {
  return useQuery<{
    entries: Item[];
  }>(
    `/directory?${stringify({
      q,
      only_my_groups: true,
      only_chat_enabled: true,
    })}`,
    { refetchOnWindowFocus: false },
  );
}

const getGroupCategoryName = (c) => get(c, ["category", "plural_name"]);
const categoryOrMemberType = (memberOrGroup: Item) =>
  memberOrGroup.type === "member"
    ? "member"
    : getGroupCategoryName(memberOrGroup);

export default function NewChat() {
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const { data, isLoading } = useAvailableChats(query);
  const sortedData = orderBy(data?.entries, "name", "asc");

  const { member, ...groupsByCategoryName } = groupBy(
    sortedData,
    categoryOrMemberType,
  );

  function submitSearch(e) {
    e.preventDefault();
  }

  const debouncedSetQuery = useCallback(
    debounce((value: string) => {
      setQuery(value);
    }, 300),
    [],
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    debouncedSetQuery(value);
  };

  const clearInput = () => {
    handleInputChange("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      clearInput();
    }
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <ChatHeader
        title={I18n.t("js.chat.new.headline")}
        ButtonLeft={<BackToRootButton />}
      />
      <div className="m-2 flex flex-col gap-2">
        <form
          className="chatNewForm flex overflow-visible"
          onSubmit={submitSearch}
        >
          <div className="chat__new_search grid stack-container w-full">
            <span className="stack-item z-50 text-muted self-center justify-self-start ml-2 px-1 h-fit aspect-square">
              <FontAwesomeIcon
                icon={isLoading ? faSpinner : faSearch}
                spin={isLoading}
                style={{ color: "#aaa" }}
              />
            </span>
            <input
              className="stack-item !px-8 z-40"
              type="text"
              name="q"
              value={inputValue}
              placeholder={I18n.t("js.chat.new.search_placeholder")}
              autoFocus
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {!isEmpty(query) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  clearInput();
                }}
                className="stack-item z-50 text-muted self-center justify-self-end mr-2 px-1 h-fit aspect-square"
              >
                <FontAwesomeIcon icon={faXmark} style={{ color: "#aaa" }} />
              </button>
            )}
          </div>
        </form>
        {isLoading && <PageLoading />}
        {isEmpty(member) || query.length === 0 ? null : (
          <GroupedItems<Item>
            key="members"
            items={member}
            renderItemComponent={(item: Item, index) => (
              <Tile key={index} {...item} />
            )}
            title={I18n.t("js.chat.new.users")}
            allowToggle={true}
          />
        )}
        {map(groupsByCategoryName, (groups, name) => [
          <GroupedItems<Item>
            key={name}
            items={groups}
            renderItemComponent={(item, index) => (
              <Tile key={index} {...item} />
            )}
            title={name}
            allowToggle={true}
          />,
        ])}
      </div>
    </>
  );
}
